import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useAlert } from "../../components/Alert";
import instance from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles({
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  tableContainer: {
    marginTop: "20px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  actionButton: {
    padding: "4px",
  },
  searchContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  searchField: {
    minWidth: "200px",
  },
  totalAmount: {
    fontWeight: "bold",
    color: "#2e7d32",
  },
  noData: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
});

const ListInvoices = () => {
  const classes = useStyles();
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await instance.get("/payments/invoices/");
      setInvoices(response.data || []);
    } catch (error) {
      showAlert("Failed to fetch invoices", "error");
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoice/${invoiceId}`);
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.invoice_number
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      invoice.created_by_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      invoice.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  if (loading) {
    return (
      <>
        <CustomAppBar />
        <Box className={classes.container}>
          <Typography>Loading invoices...</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <CustomAppBar />
      <Box className={classes.container}>
        <Typography
          variant="h4"
          style={{ marginBottom: "24px", textAlign: "center" }}
        >
          Invoice List
        </Typography>

        {/* Search and Filter Section */}
        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchField}
            label="Search invoices"
            placeholder="Search by invoice number, creator, or recipient"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
        </Box>

        {/* Invoices Table */}
        {filteredInvoices.length === 0 ? (
          <Paper className={classes.noData}>
            <Typography variant="h6">
              {invoices.length === 0
                ? "No invoices found"
                : "No invoices match your search criteria"}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  <TableCell>
                    <strong>Invoice Number</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Total Amount</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Created At</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id || invoice.invoice_number}>
                    <TableCell>{invoice.invoice_number}</TableCell>
                    <TableCell className={classes.totalAmount}>
                      ₹
                      {(
                        parseFloat(invoice.amount || 0) +
                        parseFloat(invoice.gst_amount || 0)
                      ).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {invoice.created_at
                        ? new Date(invoice.created_at).toLocaleDateString(
                            "en-IN",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Invoice Details">
                        <IconButton
                          className={classes.actionButton}
                          onClick={() =>
                            handleViewInvoice(
                              invoice.id || invoice.invoice_number
                            )
                          }
                          color="primary"
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary */}
        {filteredInvoices.length > 0 && (
          <Box style={{ marginTop: "20px", textAlign: "center" }}>
            <Typography variant="h6">
              Total Invoices: {filteredInvoices.length}
            </Typography>
            <Typography variant="h6" className={classes.totalAmount}>
              Total Amount: ₹
              {filteredInvoices
                .reduce(
                  (sum, invoice) =>
                    sum +
                    parseFloat(invoice.amount || 0) +
                    parseFloat(invoice.gst_amount || 0),
                  0
                )
                .toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ListInvoices;
